import { formatInTimeZone } from 'date-fns-tz'

export interface TimeZoneEntry {
  value: string
  abbreviation: string
  city: string
}

export const COMMON_TIMEZONES: TimeZoneEntry[] = [
  // 太平洋・ハワイ
  { value: 'Pacific/Midway', abbreviation: 'SST', city: 'ミッドウェー' },
  { value: 'Pacific/Honolulu', abbreviation: 'HST', city: 'ホノルル' },
  // 北米
  { value: 'America/Anchorage', abbreviation: 'AKST', city: 'アンカレッジ' },
  { value: 'America/Los_Angeles', abbreviation: 'PST', city: 'ロサンゼルス' },
  { value: 'America/Vancouver', abbreviation: 'PST', city: 'バンクーバー' },
  { value: 'America/Denver', abbreviation: 'MST', city: 'デンバー' },
  { value: 'America/Phoenix', abbreviation: 'MST', city: 'フェニックス' },
  { value: 'America/Chicago', abbreviation: 'CST', city: 'シカゴ' },
  { value: 'America/Mexico_City', abbreviation: 'CST', city: 'メキシコシティ' },
  { value: 'America/New_York', abbreviation: 'EST', city: 'ニューヨーク' },
  { value: 'America/Toronto', abbreviation: 'EST', city: 'トロント' },
  // 南米
  { value: 'America/Bogota', abbreviation: 'COT', city: 'ボゴタ' },
  { value: 'America/Lima', abbreviation: 'PET', city: 'リマ' },
  { value: 'America/Santiago', abbreviation: 'CLT', city: 'サンティアゴ' },
  { value: 'America/Sao_Paulo', abbreviation: 'BRT', city: 'サンパウロ' },
  { value: 'America/Buenos_Aires', abbreviation: 'ART', city: 'ブエノスアイレス' },
  // ヨーロッパ
  { value: 'Atlantic/Reykjavik', abbreviation: 'GMT', city: 'レイキャビク' },
  { value: 'Europe/London', abbreviation: 'GMT', city: 'ロンドン' },
  { value: 'Europe/Lisbon', abbreviation: 'WET', city: 'リスボン' },
  { value: 'Europe/Paris', abbreviation: 'CET', city: 'パリ' },
  { value: 'Europe/Berlin', abbreviation: 'CET', city: 'ベルリン' },
  { value: 'Europe/Amsterdam', abbreviation: 'CET', city: 'アムステルダム' },
  { value: 'Europe/Rome', abbreviation: 'CET', city: 'ローマ' },
  { value: 'Europe/Madrid', abbreviation: 'CET', city: 'マドリード' },
  { value: 'Europe/Zurich', abbreviation: 'CET', city: 'チューリッヒ' },
  { value: 'Europe/Stockholm', abbreviation: 'CET', city: 'ストックホルム' },
  { value: 'Europe/Helsinki', abbreviation: 'EET', city: 'ヘルシンキ' },
  { value: 'Europe/Athens', abbreviation: 'EET', city: 'アテネ' },
  { value: 'Europe/Istanbul', abbreviation: 'TRT', city: 'イスタンブール' },
  { value: 'Europe/Moscow', abbreviation: 'MSK', city: 'モスクワ' },
  // 中東・アフリカ
  { value: 'Africa/Cairo', abbreviation: 'EET', city: 'カイロ' },
  { value: 'Africa/Nairobi', abbreviation: 'EAT', city: 'ナイロビ' },
  { value: 'Africa/Johannesburg', abbreviation: 'SAST', city: 'ヨハネスブルグ' },
  { value: 'Asia/Dubai', abbreviation: 'GST', city: 'ドバイ' },
  { value: 'Asia/Riyadh', abbreviation: 'AST', city: 'リヤド' },
  { value: 'Asia/Tehran', abbreviation: 'IRST', city: 'テヘラン' },
  // 南アジア・東南アジア
  { value: 'Asia/Karachi', abbreviation: 'PKT', city: 'カラチ' },
  { value: 'Asia/Kolkata', abbreviation: 'IST', city: 'コルカタ' },
  { value: 'Asia/Colombo', abbreviation: 'IST', city: 'コロンボ' },
  { value: 'Asia/Dhaka', abbreviation: 'BST', city: 'ダッカ' },
  { value: 'Asia/Bangkok', abbreviation: 'ICT', city: 'バンコク' },
  { value: 'Asia/Ho_Chi_Minh', abbreviation: 'ICT', city: 'ホーチミン' },
  { value: 'Asia/Jakarta', abbreviation: 'WIB', city: 'ジャカルタ' },
  { value: 'Asia/Singapore', abbreviation: 'SGT', city: 'シンガポール' },
  { value: 'Asia/Kuala_Lumpur', abbreviation: 'MYT', city: 'クアラルンプール' },
  { value: 'Asia/Manila', abbreviation: 'PHT', city: 'マニラ' },
  // 東アジア
  { value: 'Asia/Shanghai', abbreviation: 'CST', city: '上海' },
  { value: 'Asia/Hong_Kong', abbreviation: 'HKT', city: '香港' },
  { value: 'Asia/Taipei', abbreviation: 'CST', city: '台北' },
  { value: 'Asia/Seoul', abbreviation: 'KST', city: 'ソウル' },
  { value: 'Asia/Tokyo', abbreviation: 'JST', city: '日本' },
  // オセアニア
  { value: 'Australia/Perth', abbreviation: 'AWST', city: 'パース' },
  { value: 'Australia/Adelaide', abbreviation: 'ACST', city: 'アデレード' },
  { value: 'Australia/Sydney', abbreviation: 'AEST', city: 'シドニー' },
  { value: 'Pacific/Guam', abbreviation: 'ChST', city: 'グアム' },
  { value: 'Pacific/Auckland', abbreviation: 'NZST', city: 'オークランド' },
  { value: 'Pacific/Fiji', abbreviation: 'FJT', city: 'フィジー' },
]

const tzEntryMap = Object.fromEntries(
  COMMON_TIMEZONES.map(tz => [tz.value, tz])
)

export const getTimeZoneAbbreviation = (tz: string): string => {
  return tzEntryMap[tz]?.abbreviation ?? tz
}

export const getTimeZoneLabel = (tz: string): string => {
  const entry = tzEntryMap[tz]
  if (!entry) return tz
  return `${entry.city} (${entry.abbreviation})`
}

export const getTimeZoneCity = (tz: string): string => {
  return tzEntryMap[tz]?.city ?? tz
}

export const getSecondaryHour = (
  localHour: number,
  referenceDate: Date,
  secondaryTZ: string
): { hour: number; minute: number } => {
  const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone
  const refDate = new Date(referenceDate)
  refDate.setHours(localHour, 0, 0, 0)

  const localFormatted = formatInTimeZone(refDate, localTZ, 'HH:mm')
  const secondaryFormatted = formatInTimeZone(refDate, secondaryTZ, 'HH:mm')

  const [localH, localM] = localFormatted.split(':').map(Number)
  const [secH, secM] = secondaryFormatted.split(':').map(Number)

  const localTotalMin = localH * 60 + localM
  const secTotalMin = secH * 60 + secM
  const deltaMins = secTotalMin - localTotalMin

  const targetTotalMin = ((localHour * 60 + deltaMins) % 1440 + 1440) % 1440
  return {
    hour: Math.floor(targetTotalMin / 60),
    minute: targetTotalMin % 60
  }
}
